import {
  Typography,
  Button,
  Box,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItem,
  ListItemText,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import { TagFormat, TagType } from "../../common/models/schema/TagFormat";

interface TagItemProps {
  tag: TagFormat;
  editingTag: TagFormat | null;
  updateError: string | null;
  onEditClick: (tag: TagFormat) => void;
  onEditCancel: () => void;
  onUpdateTag: () => void;
  onDeleteClick: (id: number) => void;
  onInputChange: (field: string, value: string | TagType) => void;
  getTagTypeLabel: (type: TagType) => string;
}

const TagItem = ({
  tag,
  editingTag,
  updateError,
  onEditClick,
  onEditCancel,
  onUpdateTag,
  onDeleteClick,
  onInputChange,
  getTagTypeLabel,
}: TagItemProps) => {
  const isEditing = editingTag && editingTag.id === tag.id;

  if (isEditing) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Edit Tag: {tag.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Name"
                value={editingTag.name}
                onChange={(e) => onInputChange("name", e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <TextField
                label="Format"
                value={editingTag.format}
                onChange={(e) => onInputChange("format", e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editingTag.type}
                  label="Type"
                  onChange={(e) =>
                    onInputChange("type", e.target.value as TagType)
                  }
                >
                  <MenuItem value={TagType.STRING}>
                    {getTagTypeLabel(TagType.STRING)}
                  </MenuItem>
                  <MenuItem value={TagType.LIST}>
                    {getTagTypeLabel(TagType.LIST)}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description"
              value={editingTag.description}
              onChange={(e) => onInputChange("description", e.target.value)}
              size="small"
              multiline
              rows={2}
            />
            <TextField
              label="Examples"
              value={editingTag.examples}
              onChange={(e) => onInputChange("examples", e.target.value)}
              size="small"
              multiline
              rows={2}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={onUpdateTag}
                disabled={!editingTag.name.trim() || !editingTag.format.trim()}
              >
                Update
              </Button>
              <Button variant="outlined" onClick={onEditCancel}>
                Cancel
              </Button>
            </Box>
            {updateError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {updateError}
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <ListItem sx={{ py: 2 }}>
      <ListItemText
        primary={tag.name}
        secondary={
          <Box component="span">
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              display="block"
            >
              Format: {tag.format}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              display="block"
            >
              Type: {getTagTypeLabel(tag.type)}
            </Typography>
            {tag.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                display="block"
              >
                Description: {tag.description}
              </Typography>
            )}
            {tag.examples && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                display="block"
              >
                Examples: {tag.examples}
              </Typography>
            )}
          </Box>
        }
        sx={{ minWidth: 300 }}
      />
      <Button
        onClick={() => onEditClick(tag)}
        color="primary"
        variant="outlined"
        size="small"
        sx={{ ml: 1 }}
      >
        Edit
      </Button>
      <Button
        onClick={() => onDeleteClick(tag.id)}
        color="error"
        variant="outlined"
        size="small"
        sx={{ ml: 1 }}
      >
        Delete
      </Button>
    </ListItem>
  );
};

export default TagItem;
